import { useEffect, useRef, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Portal,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { useAppDispatch } from '../../app/hooks';
import {
  api,
  useGetHomeQuery,
  usePutDisplaySettingsMutation,
} from '../../app/services/api';
import {
  DisplaySettings,
  defaultDisplaySettings,
  mergeDisplaySettings,
} from '../../types/DisplaySettings';

const numberFields: {
  key: keyof DisplaySettings;
  label: string;
  min: number;
  max: number;
}[] = [
  { key: 'cardTitleFontSize', label: '이름 글씨 크기', min: 12, max: 120 },
  { key: 'cardPositionFontSize', label: '직책 글씨 크기', min: 10, max: 80 },
  { key: 'statFontSize', label: '상단 통계 글씨 크기', min: 10, max: 60 },
  { key: 'clockFontSize', label: '시간 글씨 크기', min: 10, max: 100 },
  {
    key: 'pageRollIntervalSec',
    label: '페이지 롤링 시간(초)',
    min: 3,
    max: 120,
  },
];

type NumericKey = (typeof numberFields)[number]['key'];

type ColorKey =
  | 'presentBackgroundColor'
  | 'absentBackgroundColor'
  | 'meetingBackgroundColor'
  | 'etcBackgroundColor';

const colorFields: { key: ColorKey; label: string }[] = [
  { key: 'presentBackgroundColor', label: '재실 배경색' },
  { key: 'absentBackgroundColor', label: '부재중 배경색' },
  { key: 'meetingBackgroundColor', label: '회의중 배경색' },
  { key: 'etcBackgroundColor', label: '기타 배경색' },
];

export function DisplaySettingsButton() {
  const dispatch = useAppDispatch();
  const { data } = useGetHomeQuery();
  const [putDisplaySettings, { isLoading }] = usePutDisplaySettingsMutation();

  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [values, setValues] = useState<DisplaySettings>(
    mergeDisplaySettings(data?.displaySettings),
  );
  // Raw text shown in each numeric field while editing. Lets the field be
  // momentarily empty and strips leading zeros so "100" can't become "0100".
  const [text, setText] = useState<Partial<Record<NumericKey, string>>>({});
  const originalRef = useRef<DisplaySettings>(values);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  // Live-patch the cached home response so the dashboard reflects the change
  // instantly, before saving.
  const preview = (next: DisplaySettings) => {
    dispatch(
      api.util.updateQueryData('getHome', undefined, (draft) => {
        draft.displaySettings = next;
      }),
    );
  };

  const update = (next: DisplaySettings) => {
    setValues(next);
    preview(next);
  };

  const handleOpen = () => {
    const current = mergeDisplaySettings(data?.displaySettings);
    originalRef.current = current;
    setValues(current);
    setText({});
    setOpen(true);
  };

  const handleNumberChange = (key: NumericKey, raw: string, max: number) => {
    // digits only, drop leading zeros, allow empty while editing
    let cleaned = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
    if (cleaned !== '') {
      let n = Number(cleaned);
      if (n > max) {
        n = max;
        cleaned = String(n);
      }
      update({ ...values, [key]: n });
    }
    setText((t) => ({ ...t, [key]: cleaned }));
  };

  const handleNumberBlur = (key: NumericKey, min: number, max: number) => {
    const raw = text[key];
    const n =
      raw === undefined || raw === ''
        ? values[key]
        : Math.min(max, Math.max(min, Number(raw)));
    update({ ...values, [key]: n });
    setText((t) => ({ ...t, [key]: String(n) }));
  };

  const displayValue = (key: NumericKey) =>
    text[key] !== undefined ? text[key] : String(values[key]);

  const handleCancel = () => {
    preview(originalRef.current);
    setOpen(false);
  };

  // Close the (non-modal) panel when clicking anywhere outside it. The gear
  // button is excluded so its own handler can toggle. Disabled while the save
  // confirmation dialog is up, so interacting with the dialog can't close the
  // panel underneath it.
  const cancelRef = useRef(handleCancel);
  cancelRef.current = handleCancel;
  useEffect(() => {
    if (!open || confirmOpen) {
      return;
    }
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      cancelRef.current();
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, confirmOpen]);

  const handleReset = () => {
    // restore the factory defaults (live-previewed; user still confirms via 저장)
    setValues(defaultDisplaySettings);
    setText({});
    preview(defaultDisplaySettings);
  };

  const handleSaveConfirmed = async () => {
    try {
      await putDisplaySettings(values).unwrap();
      setConfirmOpen(false);
      setOpen(false);
    } catch {
      // Never let a failed save bubble into an uncaught rejection (which shows
      // the dev error overlay / crashes the app). Surface a friendly message
      // and keep the panel open so the user can retry.
      setConfirmOpen(false);
      setSaveError(true);
    }
  };

  return (
    <>
      <Tooltip title="화면 설정">
        <IconButton
          ref={anchorRef}
          onClick={() => (open ? handleCancel() : handleOpen())}
          sx={{ color: '#756868' }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      {open ? (
        <Portal>
          <Paper
            ref={panelRef}
            elevation={8}
            sx={{
              position: 'fixed',
              top: 72,
              right: 12,
              zIndex: (theme) => theme.zIndex.modal,
              width: 250,
              maxHeight: 'calc(100vh - 88px)',
              overflowY: 'auto',
              p: 2,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
            >
              <Typography fontWeight={700}>화면 설정</Typography>
              <IconButton size="small" onClick={handleCancel}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <Stack spacing={1.5}>
              {numberFields.map(({ key, label, min, max }) => (
                <TextField
                  key={key}
                  type="text"
                  inputMode="numeric"
                  label={label}
                  size="small"
                  value={displayValue(key)}
                  onChange={(e) => handleNumberChange(key, e.target.value, max)}
                  onBlur={() => handleNumberBlur(key, min, max)}
                  fullWidth
                />
              ))}
              {colorFields.map(({ key, label }) => (
                <Box
                  key={key}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <span>{label}</span>
                  <input
                    type="color"
                    value={values[key]}
                    onChange={(e) =>
                      update({ ...values, [key]: e.target.value })
                    }
                  />
                </Box>
              ))}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                pt={1}
              >
                <Button onClick={handleReset} color="inherit" size="small">
                  기본값
                </Button>
                <Stack direction="row" spacing={1}>
                  <Button onClick={handleCancel} color="inherit" size="small">
                    취소
                  </Button>
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    variant="contained"
                    size="small"
                    disabled={isLoading}
                  >
                    저장
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Portal>
      ) : null}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>화면 설정 저장</DialogTitle>
        <DialogContent>
          <DialogContentText>
            변경한 화면 설정을 저장하시겠습니까? 모든 화면에 적용됩니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            취소
          </Button>
          <Button
            onClick={handleSaveConfirmed}
            variant="contained"
            disabled={isLoading}
            autoFocus
          >
            저장
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={saveError}
        onClose={() => setSaveError(false)}
        autoHideDuration={4000}
        message="화면 설정 저장에 실패했습니다. 잠시 후 다시 시도해주세요."
      />
    </>
  );
}
